import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	CurrencyType,
	ValuationInputType,
	ValuationInputWithAdjustmentsRes,
} from '@dealstack/shared/lib/generated';
import { orderBy } from 'lodash';
import {
	CreateInputAdjustmentTableForm,
	SavedInputTableForm,
} from '@dealstack/frontend/app/pages/Valuations/constants.types';
import { ValuationInputDetails } from '@dealstack/frontend/app/pages/Valuations/shared/valuationInputDetails.types';

export const mapSavedToInputForm = (
	clonedFormValues: SavedInputTableForm,
	currentNumberFactor: number
): CreateInputAdjustmentTableForm => {
	// On display, the saved values are divided by the current number factor, so that it is easier for the user
	// to read and write large values.
	return {
		valuationInput: {
			...clonedFormValues.valuationInput,
			value: {
				...clonedFormValues.valuationInput.value,
				currencyAmount: clonedFormValues.valuationInput.value.currencyAmount
					.div(currentNumberFactor)
					.toFixed(5),
			},
		},
		cash: clonedFormValues.cash.map((cash) => ({
			...cash,
			value: {
				...cash.value,
				currencyAmount: cash.value.currencyAmount.div(currentNumberFactor).toFixed(5),
			},
		})),
		workingCapital: clonedFormValues.workingCapital.map((workingCapital) => ({
			...workingCapital,
			value: {
				...workingCapital.value,
				currencyAmount: workingCapital.value.currencyAmount
					.div(currentNumberFactor)
					.toFixed(5),
			},
		})),
	};
};

export const mapInputToSavedForm = (
	clonedValues: CreateInputAdjustmentTableForm,
	currentNumberFactor: number
): SavedInputTableForm => {
	// On save, we multiply the displayed values by the current number factor.
	return {
		...clonedValues,
		valuationInput: {
			...clonedValues.valuationInput,
			value: {
				...clonedValues.valuationInput.value,
				currencyAmount: new HighPrecisionDecimal(
					clonedValues.valuationInput.value.currencyAmount
				).mul(currentNumberFactor),
			},
		},
		cash: clonedValues.cash.map((cash) => ({
			...cash,
			value: {
				...cash.value,
				currencyAmount: new HighPrecisionDecimal(cash.value.currencyAmount).mul(
					currentNumberFactor
				),
			},
		})),
		workingCapital: clonedValues.workingCapital.map((workingCapital) => ({
			...workingCapital,
			value: {
				...workingCapital.value,
				currencyAmount: new HighPrecisionDecimal(workingCapital.value.currencyAmount).mul(
					currentNumberFactor
				),
			},
		})),
	};
};

export const mapApiResponseToSavedForms = (
	valuationInputsRaw: ValuationInputWithAdjustmentsRes[]
): Record<string, SavedInputTableForm> => {
	const formValues: Record<string, SavedInputTableForm> = {};

	for (const input of valuationInputsRaw) {
		formValues[input.id] = {
			valuationInput: {
				inputId: input.id,
				value: {
					currency: input.currency,
					currencyAmount: new HighPrecisionDecimal(input.currencyAmount),
				},
				inputType: input.inputType,
			},
			cash: [],
			workingCapital: [],
		};
		const sortedAdjustments = orderBy(
			input.valuationInputAdjustments,
			(adjustment) => adjustment.createdAt
		);
		for (const adjustment of sortedAdjustments) {
			const fieldName = adjustment.classifier.name === 'Cash' ? 'cash' : 'workingCapital';
			formValues[input.id][fieldName].push({
				value: {
					currency: adjustment.currency,
					currencyAmount: new HighPrecisionDecimal(adjustment.currencyAmount),
				},
				name: adjustment.name,
				adjustmentId: adjustment.id,
			});
		}
	}

	return formValues;
};

export const computeValuationInputDetails = (
	valuationInputFormValues: Record<string, SavedInputTableForm>,
	convertAmount: (
		currencyAmount: HighPrecisionDecimal,
		currency: CurrencyType
	) => HighPrecisionDecimal,
	input: ValuationInputWithAdjustmentsRes
): ValuationInputDetails | null => {
	// Convert all values within the input to the base currency, and sum up the individual parts.
	const detail: ValuationInputDetails = {
		name: input.person.displayName,
		id: input.id,
		idToConvertedValues: {},
		totalCashConverted: new HighPrecisionDecimal(0),
		totalWorkingCapitalConverted: new HighPrecisionDecimal(0),
		equityValue: new HighPrecisionDecimal(0),
	};

	if (!valuationInputFormValues[input.id]) {
		return null;
	}
	const formValues = valuationInputFormValues[input.id];

	const base = convertAmount(
		formValues.valuationInput.value.currencyAmount,
		formValues.valuationInput.value.currency
	);

	detail.idToConvertedValues[formValues.valuationInput.inputId] = base;

	// Update cash part
	const cashAdjustmentTotal = formValues.cash.reduce((acc, curr) => {
		if (!curr.adjustmentId) {
			return acc;
		}
		detail.idToConvertedValues[curr.adjustmentId] = convertAmount(
			curr.value.currencyAmount,
			curr.value.currency
		);
		detail.totalCashConverted = detail.totalCashConverted.plus(
			detail.idToConvertedValues[curr.adjustmentId]
		);
		return acc.plus(detail.idToConvertedValues[curr.adjustmentId]);
	}, new HighPrecisionDecimal(0));

	// Update working capital part
	const workingCapitalAdjustmentTotal = formValues.workingCapital.reduce((acc, curr) => {
		if (!curr.adjustmentId) {
			return acc;
		}
		detail.idToConvertedValues[curr.adjustmentId] = convertAmount(
			curr.value.currencyAmount,
			curr.value.currency
		);
		detail.totalWorkingCapitalConverted = detail.totalWorkingCapitalConverted.plus(
			detail.idToConvertedValues[curr.adjustmentId]
		);
		return acc.plus(detail.idToConvertedValues[curr.adjustmentId]);
	}, new HighPrecisionDecimal(0));

	detail.equityValue = base.plus(
		formValues.valuationInput.inputType === ValuationInputType.EQUITY_VALUE
			? new HighPrecisionDecimal(0)
			: cashAdjustmentTotal.plus(workingCapitalAdjustmentTotal)
	);

	return detail;
};
